"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { trackReviewSubmission } from "@/lib/services/analytics";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";

/**
 * Real-time festival review hook
 *
 * Modern architecture:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed (staleTime: Infinity)
 * - Automatic cross-tab/device synchronization
 */

// Custom event for syncing optimistic updates across component instances
const FESTIVAL_REVIEW_EVENT = "festival-review-update";

export interface FestivalReview {
	id: string;
	festivalId: string;
	userId: string;
	overallRating: number;
	vibeRating: number;
	productionRating: number;
	logisticsRating: number;
	foodRating: number;
	content: string | null;
	createdAt: string;
	updatedAt: string;
}

interface UseRealtimeFestivalReviewOptions {
	festivalId: string;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeFestivalReview({
	festivalId,
	userId,
	enabled = true,
}: UseRealtimeFestivalReviewOptions) {
	// Local state is our source of truth
	const [review, setReview] = useState<FestivalReview | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for saving reviews
	const saveReviewMutation = trpc.festival.saveFestivalReview.useMutation({
		onSuccess: async (_data, variables) => {
			const FORCE_REFETCH = { refetchType: 'all' as const };
			await Promise.all([
				utils.festival.getUserFestivalReview.invalidate({ festivalId }),
				utils.festival.getCommunityFestivalReviews.invalidate({ festivalId }),
				utils.festival.getFacepileData.invalidate(),
				utils.festival.getUserFestivalData.invalidate(),
				utils.festival.list.invalidate(),
				utils.festival.getBySlug.invalidate(),
				utils.user.getProfileFestivals.invalidate(undefined, FORCE_REFETCH),
				utils.user.getProfileArtists.invalidate(undefined, FORCE_REFETCH),
			]);

			const isDelete = variables.overallRating === -2;
			toast.success(isDelete ? "Review removed" : "Review saved");
		},
		onError: (err) => {
			// On error, real-time will correct the state
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to save your review"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to save review";

			toast.error(errorMessage);
		},
	});

	// Initial data fetch - happens ONCE, never goes stale
	useEffect(() => {
		if (!userId || !festivalId || !enabled) {
			setIsLoading(false);
			setReview(null);
			return;
		}

		async function fetchInitialReview() {
			if (!userId) return;
			
			try {
				type FestivalReviewRow = Database["public"]["Tables"]["festival_reviews"]["Row"];
				
				const { data, error } = await supabase
					.from("festival_reviews")
					.select("*")
					.eq("user_id", userId)
					.eq("festival_id", festivalId)
					.maybeSingle() as { data: FestivalReviewRow | null; error: any };

				if (!error && data) {
					setReview({
						id: data.id,
						festivalId: data.festival_id,
						userId: data.user_id,
						overallRating: data.overall_rating,
						vibeRating: data.vibe_rating,
						productionRating: data.production_rating,
						logisticsRating: data.logistics_rating,
						foodRating: data.food_rating,
						content: data.content,
						createdAt: data.created_at,
						updatedAt: data.updated_at,
					});
				} else {
					setReview(null);
				}
			} catch (error) {
				console.error("Error fetching initial festival review:", error);
				setReview(null);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialReview();
	}, [userId, festivalId, enabled, supabase]);

	// Real-time subscription - this is where all updates come from
	useEffect(() => {
		if (!userId || !festivalId || !enabled) return;

		// Unique channel per instance to avoid conflicts
		const channelName = `festival-review:${festivalId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "festival_reviews",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<any>) => {
					// Client-side filter for festival_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if (record?.festival_id !== festivalId) {
						return;
					}

					// Real-time update from database
					if (payload.eventType === "DELETE") {
						setReview(null);
					} else if (payload.new) {
						setReview({
							id: payload.new.id,
							festivalId: payload.new.festival_id,
							userId: payload.new.user_id,
							overallRating: payload.new.overall_rating,
							vibeRating: payload.new.vibe_rating,
							productionRating: payload.new.production_rating,
							logisticsRating: payload.new.logistics_rating,
							foodRating: payload.new.food_rating,
							content: payload.new.content,
							createdAt: payload.new.created_at,
							updatedAt: payload.new.updated_at,
						});
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully
				if (status === "CHANNEL_ERROR") {
					console.warn(
						`[Realtime] Channel error for festival review. Will retry on reconnect.`
					);
				} else if (status === "TIMED_OUT") {
					console.warn(
						`[Realtime] Connection timeout for festival review. Will retry.`
					);
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, festivalId, enabled, supabase]);

	// Listen for optimistic updates from other component instances on the same page
	useEffect(() => {
		if (!userId || !festivalId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				festivalId: eventFestivalId,
				review: eventReview,
			} = event.detail;
			// Check if this event is for our festival
			if (eventUserId === userId && eventFestivalId === festivalId) {
				setReview(eventReview);
			}
		};

		window.addEventListener(
			FESTIVAL_REVIEW_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				FESTIVAL_REVIEW_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, festivalId]);

	// Save review with optimistic updates
	const saveReview = async (data: {
		overallRating: number;
		vibeRating: number;
		productionRating: number;
		logisticsRating: number;
		foodRating: number;
		content?: string;
	}) => {
		if (!userId) {
			toast.error("Please sign in to save your review");
			return;
		}

		// Check if this is a delete operation
		const isDelete = data.overallRating === -2;

		// 1. Optimistic update - instant UI feedback
		const optimisticReview = isDelete
			? null
			: {
					id: review?.id || `temp-${festivalId}`,
					festivalId,
					userId,
					overallRating: data.overallRating,
					vibeRating: data.vibeRating,
					productionRating: data.productionRating,
					logisticsRating: data.logisticsRating,
					foodRating: data.foodRating,
					content: data.content || null,
					createdAt: review?.createdAt || new Date().toISOString(),
					updatedAt: new Date().toISOString(),
				};

		setReview(optimisticReview);

		// 2. Broadcast to other instances on the page
		const event = new CustomEvent(FESTIVAL_REVIEW_EVENT, {
			detail: {
				userId,
				festivalId,
				review: optimisticReview,
			},
		});
		window.dispatchEvent(event);

		// 3. Track analytics for review submission (not deletions)
		if (!isDelete && data.overallRating > 0) {
			trackReviewSubmission(
				festivalId,
				data.overallRating,
				"festival",
				undefined,
				!!data.content?.trim(),
			);
		}

		// 4. Send to database
		try {
			await saveReviewMutation.mutateAsync({
				festivalId,
				overallRating: data.overallRating,
				vibeRating: data.vibeRating,
				productionRating: data.productionRating,
				logisticsRating: data.logisticsRating,
				foodRating: data.foodRating,
				content: data.content,
			});
		} catch (error) {
			// Error handled in mutation onError
			// Real-time will correct the state if needed
		}

		// 5. Real-time will confirm/correct in ~100ms
	};

	// Delete review
	const deleteReview = async () => {
		// Use -2 as a special signal for deletion
		await saveReview({
			overallRating: -2,
			vibeRating: 0,
			productionRating: 0,
			logisticsRating: 0,
			foodRating: 0,
			content: "",
		});
	};

	return {
		review,
		saveReview,
		deleteReview,
		isLoading,
		isPending: saveReviewMutation.isPending,
	};
}
